import type { ApiErrorBody } from '@app/shared';

const BASE = '/api/v1';

export interface FieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Window event fired when the server says the session is gone. The auth provider listens for it. */
export const AUTH_ENDED_EVENT = 'auth:ended';

/** Time of the last successful API call; drives the session-timeout dialog. */
export const activity = { last: Date.now() };

type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue>;

export function toQueryString(query?: Query): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

function csrfToken(): string {
  const cookie = document.cookie.split('; ').find((c) => c.startsWith('csrf='));
  return cookie ? decodeURIComponent(cookie.slice('csrf='.length)) : '';
}

interface RequestOptions {
  query?: Query;
  body?: unknown;
  form?: FormData;
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken();

  let res: Response;
  try {
    res = await fetch(BASE + path + toQueryString(options.query), {
      method,
      credentials: 'include',
      headers,
      body: options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    });
  } catch {
    throw new ApiError(
      0,
      'NETWORK',
      'Cannot reach the server. Check your connection and try again.',
    );
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data: unknown = isJson ? await res.json() : undefined;

  if (!res.ok) {
    const error = (data as ApiErrorBody | undefined)?.error;
    const err = new ApiError(
      res.status,
      error?.code ?? 'ERROR',
      error?.message ?? `Request failed (${res.status})`,
      Array.isArray(error?.details) ? error.details : [],
    );
    // The customer portal's sign-in is its own: it says nothing about a staff session.
    if (res.status === 401 && err.code !== 'INVALID_CREDENTIALS' && err.code !== 'PORTAL_SIGN_IN')
      window.dispatchEvent(new CustomEvent(AUTH_ENDED_EVENT));
    throw err;
  }

  activity.last = Date.now();
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body: body ?? {} }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, { body }),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, { body }),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
  /** Multipart upload (CSV files). */
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, { form }),
  /** Absolute URL for links the browser opens itself, such as CSV downloads. */
  url: (path: string, query?: Query) => BASE + path + toQueryString(query),
};

export const errorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : 'Something went wrong.';
